import { useEffect, useRef, useState } from "react";
import { shippingInfo as defaultShippingInfo } from "../data";
import { BackIcon } from "./icons";

const KAKAO_POSTCODE_SCRIPT_ID = "kakao-daum-postcode-script";
const KAKAO_POSTCODE_SCRIPT_URL =
  "https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
const KAKAO_POSTCODE_LOAD_TIMEOUT_MS = 7000;

function getPostcodeConstructor() {
  return window.daum?.Postcode || window.kakao?.Postcode;
}

function loadKakaoPostcodeScript() {
  if (getPostcodeConstructor()) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(KAKAO_POSTCODE_SCRIPT_ID);
  if (existingScript) {
    if (existingScript.dataset.loaded === "true") {
      return Promise.resolve();
    }

    existingScript.remove();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = KAKAO_POSTCODE_SCRIPT_ID;
    script.src = KAKAO_POSTCODE_SCRIPT_URL;
    script.async = true;
    const timeoutId = window.setTimeout(() => {
      reject(new Error("Kakao postcode script load timeout"));
    }, KAKAO_POSTCODE_LOAD_TIMEOUT_MS);
    script.onload = () => {
      window.clearTimeout(timeoutId);
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      reject(new Error("Kakao postcode script load failed"));
    };
    document.head.appendChild(script);
  });
}

function buildKakaoAddress(data) {
  let address = data.roadAddress || data.jibunAddress || "";
  let extraAddress = "";

  if (data.userSelectedType === "R") {
    if (data.bname && /[동로가]$/g.test(data.bname)) {
      extraAddress += data.bname;
    }

    if (data.buildingName && data.apartment === "Y") {
      extraAddress += extraAddress ? `, ${data.buildingName}` : data.buildingName;
    }

    if (extraAddress) {
      address += ` (${extraAddress})`;
    }
  }

  return {
    postcode: data.zonecode || "",
    address,
  };
}

function RequiredLabel({ children }) {
  return (
    <label className="address-edit-label">
      <span>{children}</span>
      <span className="address-edit-required">*</span>
    </label>
  );
}

function AddressEditInput({ value, onChange, placeholder, className = "", type = "text" }) {
  return (
    <input
      className={`address-edit-input ${className}`.trim()}
      value={value}
      placeholder={placeholder}
      type={type}
      aria-label={placeholder}
      onChange={onChange}
    />
  );
}

function AddressEditField({ label, value, onChange, type = "text" }) {
  return (
    <div className="address-edit-field">
      <RequiredLabel>{label}</RequiredLabel>
      <AddressEditInput value={value} placeholder={label} type={type} onChange={onChange} />
    </div>
  );
}

function AddressSearchGroup({
  postcode,
  address,
  detailAddress,
  onPostcodeChange,
  onAddressChange,
  onDetailAddressChange,
}) {
  const postcodeLayerRef = useRef(null);
  const [isPostcodeLoading, setIsPostcodeLoading] = useState(false);
  const [isPostcodeLayerOpen, setIsPostcodeLayerOpen] = useState(false);

  useEffect(() => {
    loadKakaoPostcodeScript().catch(() => undefined);
  }, []);

  const handleAddressSearch = async () => {
    setIsPostcodeLoading(true);
    setIsPostcodeLayerOpen(true);

    try {
      await loadKakaoPostcodeScript();
      const Postcode = getPostcodeConstructor();

      if (!Postcode) {
        throw new Error("Kakao postcode script is unavailable");
      }

      window.requestAnimationFrame(() => {
        const layer = postcodeLayerRef.current;
        if (!layer) return;

        layer.innerHTML = "";

        new Postcode({
          oncomplete: (data) => {
            const nextAddress = buildKakaoAddress(data);
            onPostcodeChange(nextAddress.postcode);
            onAddressChange(nextAddress.address);
            setIsPostcodeLayerOpen(false);
            setIsPostcodeLoading(false);
          },
          width: "100%",
          height: "100%",
          maxSuggestItems: 5,
        }).embed(layer);

        setIsPostcodeLoading(false);
      });
    } catch {
      setIsPostcodeLoading(false);
      setIsPostcodeLayerOpen(false);
      window.alert("주소 검색 서비스를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  return (
    <div className="address-edit-field">
      <RequiredLabel>배송 주소</RequiredLabel>

      <div className="address-edit-search-row">
        <AddressEditInput
          value={postcode}
          placeholder="우편번호 찾기"
          onChange={(event) => onPostcodeChange(event.target.value)}
        />
        <button
          className="address-edit-search-button"
          type="button"
          onClick={handleAddressSearch}
          disabled={isPostcodeLoading}
        >
          {isPostcodeLoading ? "검색 중" : "주소 검색"}
        </button>
      </div>

      <AddressEditInput
        className="address-edit-address-line"
        value={address}
        placeholder="기본 주소"
        onChange={(event) => onAddressChange(event.target.value)}
      />
      <AddressEditInput
        className="address-edit-address-line"
        value={detailAddress}
        placeholder="상세 주소"
        onChange={(event) => onDetailAddressChange(event.target.value)}
      />

      {isPostcodeLayerOpen ? (
        <div className="address-postcode-layer">
          <header className="address-postcode-header">
            <strong>주소 검색</strong>
            <button type="button" onClick={() => setIsPostcodeLayerOpen(false)}>
              닫기
            </button>
          </header>
          <div className="address-postcode-body" ref={postcodeLayerRef} />
        </div>
      ) : null}
    </div>
  );
}

function AddressEditStatusBar() {
  return (
    <div className="address-edit-statusbar" aria-hidden="true">
      <span>9:41</span>
      <div className="address-edit-status-icons">
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

function AddressEditHeader({ onBack }) {
  return (
    <header className="address-edit-header">
      <button className="address-edit-back" type="button" aria-label="뒤로가기" onClick={onBack}>
        <BackIcon />
      </button>
      <h2>배송정보 수정</h2>
    </header>
  );
}

export function ShippingInfoEditPanel({ shippingInfo = defaultShippingInfo, onClose, onSave }) {
  const [recipient, setRecipient] = useState(shippingInfo.recipient || shippingInfo.receiver || "");
  const [phone, setPhone] = useState(shippingInfo.phone || shippingInfo.editPhone || "");
  const [postcode, setPostcode] = useState(shippingInfo.postcode || "");
  const [address, setAddress] = useState(shippingInfo.addressLine1 || shippingInfo.baseAddress || shippingInfo.address || "");
  const [detailAddress, setDetailAddress] = useState(shippingInfo.detail || shippingInfo.addressLine2 || "");

  const handleSave = () => {
    if (!recipient.trim() || !phone.trim() || !address.trim()) {
      window.alert("받으시는 분, 휴대폰 번호, 배송 주소를 입력해 주세요.");
      return;
    }

    onSave({
      ...shippingInfo,
      recipient: recipient.trim(),
      phone: phone.trim(),
      postcode: postcode.trim(),
      addressLine1: address.trim(),
      addressLine2: detailAddress.trim(),
    });
  };

  return (
    <section className="shipping-info-edit-panel" data-node-id="6359:19183">
      <AddressEditStatusBar />
      <AddressEditHeader onBack={onClose} />

      <main className="address-edit-content">
        <section className="address-edit-section">
          <h3>배송지</h3>

          <div className="address-edit-fields">
            <AddressEditField
              label="받으시는 분"
              value={recipient}
              onChange={(event) => setRecipient(event.target.value)}
            />
            <AddressEditField
              label="휴대폰 번호"
              value={phone}
              type="tel"
              onChange={(event) => setPhone(event.target.value)}
            />
            <AddressSearchGroup
              postcode={postcode}
              address={address}
              detailAddress={detailAddress}
              onPostcodeChange={setPostcode}
              onAddressChange={setAddress}
              onDetailAddressChange={setDetailAddress}
            />
          </div>
        </section>
      </main>

      <footer className="address-edit-savebar">
        <button className="address-edit-save" type="button" onClick={handleSave}>
          저장
        </button>
      </footer>
    </section>
  );
}
